import React, { createContext, useContext, useMemo, useState, useEffect } from 'react'
import ru from './translations/ru'
import en from './translations/en'

const LANG_KEY = 'admin_lang'

const translations = { ru, en }

// Global translate function for use outside React context (e.g., api/client.js)
let globalLang = 'ru'
let globalT = (key, params) => {
  const dict = translations[globalLang] || translations.ru
  const fallbackDict = globalLang === 'en' ? null : translations.en
  const template =
    (dict && Object.prototype.hasOwnProperty.call(dict, key) && dict[key]) ||
    (fallbackDict && Object.prototype.hasOwnProperty.call(fallbackDict, key) && fallbackDict[key]) ||
    key
  if (!params) return template
  return Object.keys(params).reduce(
    (s, k) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(params[k])),
    template,
  )
}

export function setGlobalLang(lang) {
  globalLang = lang === 'en' ? 'en' : 'ru'
}

export function getT() {
  return globalT
}

const I18nContext = createContext({
  lang: 'ru',
  t: (key, params) => key,
  setLang: () => {},
})

function interpolate(template, params) {
  if (!params) return template
  return Object.keys(params).reduce(
    (s, k) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(params[k])),
    template,
  )
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem(LANG_KEY)
    if (saved === 'ru' || saved === 'en') return saved
    if (typeof navigator !== 'undefined') {
      const nav = navigator.language || navigator.userLanguage || ''
      return nav.toLowerCase().startsWith('ru') ? 'ru' : 'en'
    }
    return 'ru'
  })

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang)
    setGlobalLang(lang) // Sync global lang for use outside React context
  }, [lang])

  const value = useMemo(() => {
    function t(key, params) {
      const dict = translations[lang] || translations.ru
      const fallbackDict = lang === 'en' ? null : translations.en
      const template =
        (dict && Object.prototype.hasOwnProperty.call(dict, key) && dict[key]) ||
        (fallbackDict && Object.prototype.hasOwnProperty.call(fallbackDict, key) && fallbackDict[key]) ||
        key
      return typeof template === 'string' ? interpolate(template, params) : template
    }

    function setLang(next) {
      setLangState(next === 'en' ? 'en' : 'ru')
    }

    return { lang, t, setLang }
  }, [lang])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  return useContext(I18nContext)
}
